import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';

/**
 * Exporta um conjunto de slides DOM como PDF nativo do LinkedIn.
 *
 * Cada slide DOM precisa ter dimensões reais 1080×1350px (formato 4:5 do
 * LinkedIn document post). Renderiza fora da tela (offscreen) e exporta.
 *
 * Uso: chame com array de elementos DOM já renderizados em export mode.
 */
export async function exportCarouselAsPdf(
  slideElements: HTMLElement[],
  filename = 'carrossel.pdf',
): Promise<void> {
  if (slideElements.length === 0) {
    throw new Error('Nenhum slide pra exportar.');
  }

  // jsPDF aceita dimensões customizadas via opção 'format'. LinkedIn document
  // posts aceitam praticamente qualquer proporção; 4:5 (1080×1350) é o padrão
  // de carrossel vertical mobile-first.
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [1080, 1350],
    compress: true,
    hotfixes: ['px_scaling'],
  });

  for (let i = 0; i < slideElements.length; i += 1) {
    const el = slideElements[i];
    if (!el) continue;

    // Renderiza o DOM em canvas. Scale 2x pra qualidade retina sem inflar
    // o PDF — html2canvas + jsPDF lidam bem com isso.
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: '#f4efe6',
      useCORS: true,
      logging: false,
      width: 1080,
      height: 1350,
      windowWidth: 1080,
      windowHeight: 1350,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.92);

    if (i > 0) {
      pdf.addPage([1080, 1350], 'portrait');
    }
    pdf.addImage(imgData, 'JPEG', 0, 0, 1080, 1350, undefined, 'FAST');
  }

  pdf.save(filename);
}

/**
 * Exporta os slides como ZIP contendo PNGs individuais (1080×1350 cada).
 *
 * Formato pensado pro **carrossel de imagens nativo do LinkedIn**: você sobe
 * múltiplas imagens (até 9) num único post, e o LinkedIn renderiza como
 * carrossel swipável horizontal. Visual mais limpo que PDF document post.
 *
 * Como subir no LinkedIn:
 *   1. Extrai o ZIP
 *   2. "Iniciar publicação" → ícone de imagem
 *   3. Seleciona TODAS as imagens em ordem (slide-01.png, slide-02.png, ...)
 *   4. LinkedIn gera o carrossel automaticamente
 */
export async function exportCarouselAsImagesZip(
  slideElements: HTMLElement[],
  filename = 'carrossel-imagens.zip',
): Promise<void> {
  if (slideElements.length === 0) {
    throw new Error('Nenhum slide pra exportar.');
  }

  const zip = new JSZip();
  const folder = zip.folder('carrossel');
  if (!folder) throw new Error('Falha ao criar pasta no ZIP.');

  for (let i = 0; i < slideElements.length; i += 1) {
    const el = slideElements[i];
    if (!el) continue;

    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: '#f4efe6',
      useCORS: true,
      logging: false,
      width: 1080,
      height: 1350,
      windowWidth: 1080,
      windowHeight: 1350,
    });

    // toBlob é async; convertemos pra Promise.
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png', 1.0);
    });
    if (!blob) {
      throw new Error(`Falha ao gerar PNG do slide ${i + 1}.`);
    }

    const slideNum = (i + 1).toString().padStart(2, '0');
    const total = slideElements.length.toString().padStart(2, '0');
    folder.file(`slide-${slideNum}-de-${total}.png`, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

  // Trigger download
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
